/* ==========================================================================
   <farmahanem-trendyol-showcase> — anasayfa Trendyol yorum vitrini
   --------------------------------------------------------------------------
   Mağaza özeti + yorum kartları. İki mod:
   - Ürün seçiliyse ([data-fh-ty-products]): her ürünün barkoduyla o ürünün
     en iyi yorumu (ürün başına 1 istek); kartta ürün görseli + adı + linki.
   - Ürün yoksa: barkodsuz çağrı = mağaza geneli yorumlar (1 istek, ürünsüz).
   İstekler bölüm ekrana yaklaşınca atılır; yanıtlar 1 saat oturumda saklanır
   (veri günlük tazeleniyor). Veri gelmezse / yorum yoksa bölüm gizlenir;
   tema editöründe bunun yerine bilgi mesajı görünür.
   Yorum metinleri HTML olarak değil düz metin (textContent) olarak basılır.
   Kullanım: sections/farmahanem-trendyol-reviews.liquid
   Doküman:  docs/trendyol-yorum-api-shopify-new.md
   ========================================================================== */

const CACHE_MS = 60 * 60 * 1000;
const MAX_PAGE_SIZE = 100;
const PER_PRODUCT_PAGE_SIZE = 5;
const STAR_PATH = 'M10 1.6l2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L1.6 7.7l5.8-.8z';

function el(tag, { className, text, attrs } = {}, children = []) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  if (attrs) Object.entries(attrs).forEach(([key, value]) => node.setAttribute(key, value));
  children.filter(Boolean).forEach((child) => node.append(child));
  return node;
}

/** 0–5 arası puan için 5 yıldız (dolu oranı genişlikle; yarım yıldız doğru çıkar). */
function stars(rating, ariaLabel) {
  const icons = Array.from({ length: 5 }, () => `<svg viewBox="0 0 20 20" focusable="false"><path d="${STAR_PATH}"/></svg>`).join('');
  const fill = Math.max(0, Math.min(100, (Number(rating) / 5) * 100));
  const wrap = el('span', { className: 'fh-ty-home__star-box', attrs: { role: 'img', 'aria-label': ariaLabel } });
  wrap.innerHTML = `<span class="fh-ty-home__star-row" aria-hidden="true">${icons}</span>`
    + `<span class="fh-ty-home__star-row fh-ty-home__star-row--fill" style="width:${fill}%" aria-hidden="true">${icons}</span>`;
  return wrap;
}

class FarmahanemTrendyolShowcase extends HTMLElement {
  connectedCallback() {
    if (this.dataset.fhReady === 'true') return;
    this.dataset.fhReady = 'true';

    this.api = (this.dataset.api || '').replace(/\/$/, '');
    this.count = Math.max(1, Number(this.dataset.count) || 8);
    this.sort = this.dataset.sort === 'newest' ? 'newest' : 'likes';
    this.rate = Number(this.dataset.rate) || 0;
    this.minLength = Number(this.dataset.minLength) || 0;
    this.section = this.closest('.fh-ty-home');
    this.track = this.querySelector('[data-fh-ty-track]');
    this.locale = document.documentElement.lang || 'tr';
    this.dateFormat = new Intl.DateTimeFormat(this.locale, { day: 'numeric', month: 'long', year: 'numeric' });
    try {
      this.labels = JSON.parse(this.querySelector('[data-fh-ty-labels]').textContent);
    } catch (error) {
      this.labels = {};
    }
    try {
      this.products = JSON.parse(this.querySelector('[data-fh-ty-products]').textContent)
        .filter((product) => product && product.barcode);
    } catch (error) {
      this.products = [];
    }

    this.onClick = this.onClick.bind(this);
    this.updateArrows = this.updateArrows.bind(this);
    this.addEventListener('click', this.onClick);
    this.track.addEventListener('scroll', this.updateArrows, { passive: true });

    if (!this.api) {
      this.fail();
      return;
    }
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.load();
      }, { rootMargin: '600px 0px' });
      this.observer.observe(this);
    } else {
      this.load();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    if (this.track) this.track.removeEventListener('scroll', this.updateArrows);
    if (this.observer) this.observer.disconnect();
  }

  label(key, vars = {}) {
    return Object.entries(vars).reduce(
      (text, [name, value]) => text.replaceAll(`{{ ${name} }}`, value),
      this.labels[key] || ''
    );
  }

  async getJson(url) {
    const key = `fh-ty:${url}`;
    try {
      const hit = JSON.parse(sessionStorage.getItem(key));
      if (hit && Date.now() - hit.t < CACHE_MS) return hit.d;
    } catch (error) {
      /* storage kapalı — önbelleksiz devam */
    }
    const response = await fetch(url, { credentials: 'omit' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    try {
      sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), d: data }));
    } catch (error) {
      /* kota dolu / storage kapalı */
    }
    return data;
  }

  listUrl(pageSize, barcode) {
    const params = new URLSearchParams({ sort: this.sort, page: '1', pageSize: String(pageSize) });
    if (this.rate) params.set('rate', String(this.rate));
    if (barcode) params.set('barcode', barcode);
    return `${this.api}?${params}`;
  }

  isLongEnough(review) {
    return (review.comment || '').trim().length >= this.minLength;
  }

  /** Seçilen ürünlerin her biri için en iyi yorum: [{ review, product }]. */
  async loadByProducts() {
    const results = await Promise.all(this.products.map((product) => this
      .getJson(this.listUrl(PER_PRODUCT_PAGE_SIZE, product.barcode))
      .then((data) => {
        const reviews = data.result || [];
        const review = reviews.find((item) => this.isLongEnough(item)) || reviews[0];
        return review ? { review, product } : null;
      })
      // Tek ürünün isteği düşerse diğer kartlar yine gösterilsin.
      .catch(() => null)));
    return results.filter(Boolean).slice(0, this.count);
  }

  /** Mağaza geneli (ürünsüz). Kısa yorumlar elenebileceği için fazlası istenir. */
  async loadStoreWide() {
    const data = await this.getJson(this.listUrl(Math.min(MAX_PAGE_SIZE, this.count * 4)));
    return (data.result || [])
      .filter((review) => this.isLongEnough(review))
      .slice(0, this.count)
      .map((review) => ({ review, product: null }));
  }

  async load() {
    try {
      const [summary, entries] = await Promise.all([
        this.getJson(`${this.api}/summary`),
        this.products.length ? this.loadByProducts() : this.loadStoreWide()
      ]);
      if (!entries.length) throw new Error('no reviews');

      this.renderSummary(summary.result || {});
      this.track.replaceChildren(...entries.map((entry) => this.renderCard(entry.review, entry.product)));
      this.classList.add('fh-ty-home__wrap--ready');
      this.updateArrows();
    } catch (error) {
      console.error('[farmahanem-trendyol-showcase]', error);
      this.fail();
    }
  }

  /** Veri yok: ziyaretçiye boş blok gösterme. Editörde sebebini söyle. */
  fail() {
    if (this.hasAttribute('data-design-mode')) {
      this.replaceChildren(el('p', { className: 'fh-ty-home__unavailable', text: this.label('unavailable') }));
      return;
    }
    if (this.section) this.section.hidden = true;
  }

  renderSummary(summary) {
    const total = Number(summary.total) || 0;
    const average = Number(summary.average) || 0;
    const averageText = average.toLocaleString(this.locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    const averageEl = this.querySelector('[data-fh-ty-average]');
    const starsEl = this.querySelector('[data-fh-ty-stars]');
    const totalEl = this.querySelector('[data-fh-ty-total]');
    if (averageEl) averageEl.textContent = averageText;
    if (starsEl) starsEl.replaceChildren(stars(average, this.label('star_aria', { rating: averageText })));
    if (totalEl) {
      totalEl.textContent = this.label(total === 1 ? 'count_one' : 'count_other', { count: total.toLocaleString(this.locale) });
    }
  }

  /** Kartın üstündeki ürün satırı: görsel + ad + "Ürünü incele" (tek link). */
  renderProduct(product) {
    if (!product || !product.url) return null;
    const image = typeof product.image === 'string' && product.image
      ? el('img', { className: 'fh-ty-home__product-img', attrs: { src: product.image, alt: '', loading: 'lazy', decoding: 'async', width: '48', height: '48' } })
      : el('span', { className: 'fh-ty-home__product-img', attrs: { 'aria-hidden': 'true' } });
    return el('a', { className: 'fh-ty-home__product', attrs: { href: product.url } }, [
      image,
      el('span', { className: 'fh-ty-home__product-text' }, [
        el('span', { className: 'fh-ty-home__product-title', text: product.title || '' }),
        el('span', { className: 'fh-ty-home__product-link', text: this.label('view_product') })
      ])
    ]);
  }

  renderCard(review, product) {
    const name = (review.user_name || '').trim();
    const first = name.charAt(0);
    const rating = Number(review.rate) || 0;
    const likes = Number(review.like_count) || 0;
    const date = review.created_at ? new Date(review.created_at) : null;
    const validDate = date && !Number.isNaN(date.getTime()) ? date : null;

    return el('li', { className: 'fh-ty-home__item' }, [
      el('article', { className: 'fh-ty-home__card' }, [
        this.renderProduct(product),
        stars(rating, this.label('star_aria', { rating: String(rating) })),
        el('p', { className: 'fh-ty-home__quote', text: (review.comment || '').trim() }),
        el('footer', { className: 'fh-ty-home__meta' }, [
          // Maskeli adlar "**** ****" olabilir; harf yoksa nötr işaret.
          el('span', { className: 'fh-ty-home__avatar', text: /\p{L}/u.test(first) ? first.toLocaleUpperCase(this.locale) : '•', attrs: { 'aria-hidden': 'true' } }),
          el('span', { className: 'fh-ty-home__who' }, [
            el('strong', { className: 'fh-ty-home__name', text: name }),
            validDate ? el('time', { className: 'fh-ty-home__date', text: this.dateFormat.format(validDate), attrs: { datetime: validDate.toISOString() } }) : null
          ]),
          likes > 0 ? el('span', { className: 'fh-ty-home__likes', text: this.label('likes', { count: likes.toLocaleString(this.locale) }) }) : null
        ])
      ])
    ]);
  }

  updateArrows() {
    const prev = this.querySelector('[data-fh-ty-scroll="prev"]');
    const next = this.querySelector('[data-fh-ty-scroll="next"]');
    if (!prev || !next) return;
    const max = this.track.scrollWidth - this.track.clientWidth;
    prev.disabled = this.track.scrollLeft <= 2;
    next.disabled = this.track.scrollLeft >= max - 2;
  }

  onClick(event) {
    const button = event.target.closest('[data-fh-ty-scroll]');
    if (!button) return;
    const direction = button.dataset.fhTyScroll === 'prev' ? -1 : 1;
    this.track.scrollBy({ left: direction * this.track.clientWidth * 0.9, behavior: 'smooth' });
  }
}

if (!customElements.get('farmahanem-trendyol-showcase')) {
  customElements.define('farmahanem-trendyol-showcase', FarmahanemTrendyolShowcase);
}
